import {
  CapitalSocialSnapshot,
  RepresentativeSnapshot,
  ShareNumberingSnapshot,
  SocioSnapshot,
} from '../../operaciones-societarias-form/models/constitution-snapshot.model';
import { SocioRow } from '../models/socio-row';
import { formatTituloLabel } from './titulo-label.util';
import {
  partnerTotalAdjustedShares,
  partnerTotalVnEuro,
  totalCapitalSocialEuro,
} from './socios-display.util';
import {
  formatClassificationLabel,
  formatSelfEmployedChargedToLabel,
} from './admin-display.util';

function mapRepresentatives(reps: any[] | null | undefined): RepresentativeSnapshot[] {
  return (reps ?? [])
    .map((r: any) => {
      const first = r?.firstSurname ?? r?.first_surname ?? '';
      const second = r?.secondSurname ?? r?.second_surname ?? '';
      const given = r?.name ?? '';
      const fullName = [first, second, given].filter(Boolean).join(' ').trim() || given || '';
      const nif = r?.documentNumber ?? r?.document_number ?? r?.nif ?? '';
      if (!fullName && !nif) return null;
      return { name: fullName, nif };
    })
    .filter((x): x is RepresentativeSnapshot => x != null);
}

function mapShareNumberings(raw: any[], capital?: CapitalSocialSnapshot): ShareNumberingSnapshot[] {
  return (raw ?? []).map((sn: any) => {
    const seriesId = sn.seriesId ?? sn.series?.id ?? sn.socialCapital?.id ?? null;
    let seriesName = sn.seriesName || sn.series?.name || sn.socialCapital?.name || '';
    if (!seriesName && seriesId != null && capital) {
      const idx = Number(seriesId);
      const byIdx = capital.series[idx];
      if (byIdx) seriesName = byIdx.nombre;
    }
    return {
      seriesId,
      seriesName,
      sharesCount: sn.sharesCount ?? sn.sharesNum ?? 0,
      sharesNumberFrom: sn.sharesNumberFrom ?? sn.shareFrom ?? 0,
      sharesNumberTo: sn.sharesNumberTo ?? sn.shareTo ?? 0,
      titulo: sn.titulo || 'plena_propiedad',
      ownershipPercentage: sn.ownershipPercentage ?? null,
    };
  });
}

function computeParticipationPercent(
  socio: { shareNumberings?: ShareNumberingSnapshot[]; numAcciones?: number },
  capital: CapitalSocialSnapshot,
  allSocios?: SocioSnapshot[],
): string {
  const totalCapitalEuro = totalCapitalSocialEuro(capital);
  if (totalCapitalEuro > 0 && socio.shareNumberings?.length) {
    const vn = partnerTotalVnEuro(capital, socio.shareNumberings);
    return (
      ((vn / totalCapitalEuro) * 100).toLocaleString('es-ES', {
        minimumFractionDigits: 4,
        maximumFractionDigits: 4,
        useGrouping: true,
      }) + ' %'
    );
  }
  if (allSocios?.length) {
    const totalShares = allSocios.reduce((sum, s) => sum + (s.numAcciones || 0), 0);
    const numAcc = socio.numAcciones ?? 0;
    return totalShares > 0
      ? ((numAcc / totalShares) * 100).toLocaleString('es-ES', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
          useGrouping: true,
        }) + ' %'
      : '—';
  }
  return '—';
}

function buildSocioSnapshotBase(
  data: {
    name?: string;
    documentNumber?: string;
    address?: string;
    associatedClientName?: string;
    representatives?: RepresentativeSnapshot[];
    shareNumberings?: ShareNumberingSnapshot[];
    isWorkerPartner?: boolean;
    socialSecurityRegime?: string;
    classification?: string;
    selfEmployedChargedTo?: string;
    selfEmployedChargedName?: string;
    iban?: string;
    monthlyNetIncome?: number | null;
    mutualAccidents?: string;
    mutualCommonContingencies?: string;
    isGeneralMeetingPresident?: boolean;
    isGeneralMeetingSecretary?: boolean;
    observations?: string;
    sharesCount?: number;
  },
  capital: CapitalSocialSnapshot,
  allSocios?: SocioSnapshot[],
): SocioSnapshot {
  const shareNumberings = data.shareNumberings ?? [];
  const numAcc =
    shareNumberings.reduce((a, n) => a + (n.sharesCount ?? 0), 0) ||
    data.sharesCount ||
    0;
  const firstTitulo = shareNumberings[0]?.titulo ?? 'plena_propiedad';

  const snapshot: SocioSnapshot = {
    nombre: data.name ?? '',
    nif: data.documentNumber ?? '',
    numAcciones: numAcc,
    titulo: formatTituloLabel(firstTitulo),
    porcentajeParticipacion: computeParticipationPercent(
      { shareNumberings, numAcciones: numAcc },
      capital,
      allSocios,
    ),
    esPresidente: data.isGeneralMeetingPresident ?? false,
    esSecretario: data.isGeneralMeetingSecretary ?? false,
    domicilio: data.address,
    associatedClientName: data.associatedClientName,
    representatives: data.representatives,
    shareNumberings: shareNumberings.length > 0 ? shareNumberings : undefined,
    esSocioTrabajador: data.isWorkerPartner ?? false,
    regimenSeguridadSocial: data.socialSecurityRegime,
    encuadramiento: formatClassificationLabel(data.classification),
    autonomosACargoDe: formatSelfEmployedChargedToLabel(data.selfEmployedChargedTo),
    autonomosACargoNombre: data.selfEmployedChargedName,
    iban: data.iban,
    rendimientoNetoMensual: data.monthlyNetIncome,
    mutuaAcc: data.mutualAccidents,
    mutuaCcIt: data.mutualCommonContingencies,
    observaciones: data.observations,
  };

  return snapshot;
}

export function mapPartnerApiToSocioSnapshot(
  partner: any,
  capital: CapitalSocialSnapshot,
  allPartners?: any[],
): SocioSnapshot {
  const rawShares = Array.isArray(partner?.partnerShares)
    ? partner.partnerShares
    : Array.isArray(partner?.shareNumberings)
      ? partner.shareNumberings
      : [];
  const shareNumberings = mapShareNumberings(rawShares, capital);
  const representatives = mapRepresentatives(partner?.representatives);

  const allSocios = allPartners?.map(p =>
    mapPartnerApiToSocioSnapshot(p, capital),
  );

  return buildSocioSnapshotBase(
    {
      name: partner?.name,
      documentNumber: partner?.documentNumber,
      address: partner?.address,
      associatedClientName: partner?.associatedClient?.name,
      representatives,
      shareNumberings,
      isWorkerPartner: partner?.isWorkerPartner,
      socialSecurityRegime: partner?.socialSecurityRegime,
      classification: partner?.classification,
      selfEmployedChargedTo: partner?.selfEmployedChargedTo,
      selfEmployedChargedName: partner?.selfEmployedChargedName,
      iban: partner?.bankAccount?.iban ?? partner?.iban,
      monthlyNetIncome: partner?.monthlyNetIncome,
      mutualAccidents: partner?.mutualAccidents,
      mutualCommonContingencies: partner?.mutualCommonContingencies,
      isGeneralMeetingPresident: partner?.isGeneralMeetingPresident,
      isGeneralMeetingSecretary: partner?.isGeneralMeetingSecretary,
      observations: partner?.observations,
      sharesCount: partner?.totalShares ?? partner?.sharesCount,
    },
    capital,
    allSocios,
  );
}

export function mapOperationSocioToSocioSnapshot(
  socio: any,
  capital: CapitalSocialSnapshot,
  allSocios?: any[],
): SocioSnapshot {
  const shareNumberings = mapShareNumberings(socio?.shareNumberings ?? [], capital);
  const representatives = (socio?.representatives ?? [])
    .map((r: any) => ({
      name: r?.name ?? '',
      nif: r?.nif ?? r?.documentNumber ?? '',
    }))
    .filter((r: RepresentativeSnapshot) => r.name || r.nif);

  const mappedAll = allSocios?.map(s => mapOperationSocioToSocioSnapshot(s, capital));

  return buildSocioSnapshotBase(
    {
      name: socio?.name,
      documentNumber: socio?.documentNumber,
      address: socio?.address,
      representatives,
      shareNumberings,
      isWorkerPartner: socio?.isWorkerPartner,
      socialSecurityRegime: socio?.socialSecurityRegime,
      classification: socio?.classification,
      selfEmployedChargedTo: socio?.selfEmployedChargedTo,
      selfEmployedChargedName: socio?.selfEmployedChargedName,
      iban: socio?.iban ?? socio?.bankAccount,
      monthlyNetIncome: socio?.monthlyNetIncome,
      mutualAccidents: socio?.mutualAccidents,
      mutualCommonContingencies: socio?.mutualCommonContingencies,
      isGeneralMeetingPresident: socio?.isGeneralMeetingPresident,
      isGeneralMeetingSecretary: socio?.isGeneralMeetingSecretary,
      observations: socio?.observations,
      sharesCount: socio?.sharesCount,
    },
    capital,
    mappedAll,
  );
}

export function mapSocioRowToSnapshot(
  socio: SocioRow,
  capital: CapitalSocialSnapshot,
): SocioSnapshot {
  const shareNumberings: ShareNumberingSnapshot[] = (socio.shareNumberings ?? []).map(sn => ({
    seriesId: sn.seriesId,
    seriesName: sn.seriesName ?? '',
    sharesCount: sn.sharesCount,
    sharesNumberFrom: sn.sharesNumberFrom,
    sharesNumberTo: sn.sharesNumberTo,
    titulo: sn.titulo ?? 'plena_propiedad',
    ownershipPercentage: sn.ownershipPercentage ?? null,
  }));

  const representatives: RepresentativeSnapshot[] = (socio.representatives ?? []).map(r => ({
    name: r.name,
    nif: r.nif ?? '',
  }));

  const snapshot = buildSocioSnapshotBase(
    {
      name: socio.name,
      documentNumber: socio.documentNumber,
      address: socio.address,
      representatives,
      shareNumberings,
      isWorkerPartner: socio.isWorkerPartner,
      socialSecurityRegime: socio.socialSecurityRegime,
      classification: socio.classification,
      selfEmployedChargedTo: socio.selfEmployedChargedTo,
      selfEmployedChargedName: socio.selfEmployedChargedName,
      iban: socio.bankAccount != null ? String(socio.bankAccount) : undefined,
      monthlyNetIncome: socio.monthlyNetIncome,
      mutualAccidents: socio.mutualAccidents,
      mutualCommonContingencies: socio.mutualCommonContingencies,
      isGeneralMeetingPresident: socio.isGeneralMeetingPresident,
      isGeneralMeetingSecretary: socio.isGeneralMeetingSecretary,
      observations: socio.observations,
      sharesCount: socio.sharesCount ?? undefined,
    },
    capital,
  );

  const totalCapitalEuro = totalCapitalSocialEuro(capital);
  if (totalCapitalEuro > 0 && shareNumberings.length) {
    snapshot.porcentajeParticipacion = computeParticipationPercent(
      { shareNumberings },
      capital,
    );
  }

  return snapshot;
}

export function mapSociosApiToSnapshots(
  partners: any[],
  capital: CapitalSocialSnapshot,
): SocioSnapshot[] {
  return partners.map(p => mapPartnerApiToSocioSnapshot(p, capital, partners));
}

export function mapSociosOperationToSnapshots(
  socios: any[],
  capital: CapitalSocialSnapshot,
): SocioSnapshot[] {
  return socios.map(s => mapOperationSocioToSocioSnapshot(s, capital, socios));
}
